import { InputPropsBase, SelectPropsBase } from '../interfaces/Inputs';
import { Item } from '../interfaces/Item';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import './styles.scss';
import { Label } from '../Label';
import { DEFAULT_FOCUS_VALUE } from '../../hooks/useRoveFocus';
import { KEY_ARROW_UP, KEY_ENTER, KEY_TAB, Variant } from '../models';
import TsrTooltip from '../../tooltip';
import { SearchQuery } from '.';
import { letterWidth } from '../../consts/letterConsts';

interface SelectInputListProps extends InputPropsBase<Item | string | number>, SelectPropsBase {
    onActive: (value: boolean) => void;
    width?: number | string;
    searchQuery?: SearchQuery;
    setFocusItem: React.Dispatch<React.SetStateAction<number>>;
    focusItem: number;
    isMenuOpen?: boolean;
    searchRef: React.RefObject<HTMLInputElement>;
    isSearchBar?: boolean;
    refContainer?: React.RefObject<HTMLDivElement>;
}

export const SelectInputList = React.forwardRef((props: SelectInputListProps, ref: React.LegacyRef<HTMLDivElement>) => {
    const maxWidth =
        props.refContainer?.current && props.refContainer?.current?.offsetWidth > 300
            ? props.refContainer?.current?.offsetWidth
            : 300;
    const {
        searchQuery = { value: '', shouldSearch: false },
        isSearchBar,
        searchRef,
        setFocusItem,
        focusItem,
        refContainer,
    } = props;
    const isStringArray = props.options && typeof props.options[0] === 'string';
    const results = useMemo(() => {
        if (!searchQuery.shouldSearch) return props.options || [];
        return !searchQuery.value && props.options && isStringArray
            ? (props.options as Array<string>).filter(item => item.toLowerCase().includes(searchQuery.value.toLowerCase()))
            : (props.options as Array<Item>).filter((item: { label: string }) =>
                  item.label.toLowerCase().includes(searchQuery.value.toLowerCase()),
              );
    }, [isStringArray, props.options, searchQuery]);
    const [disableMousePointer, setDisableMousePointer] = useState(false);
    const activeRef = useRef<HTMLDivElement>(null);

    const selectOptionHadler = (item: Item | string, index: number) => {
        props.onActive(false);
        props.onChange && props.onChange(item);
        setFocusItem(index);
        refContainer?.current?.focus();
    };

    const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>, item: Item | string, index: number) => {
        setDisableMousePointer(true);
        if (event.key === KEY_ENTER) {
            if (item) {
                selectOptionHadler(item, index);
            }
        } else if (event.key === KEY_ARROW_UP && index === 0) {
            searchRef.current?.focus();
            isSearchBar && setFocusItem(DEFAULT_FOCUS_VALUE);
        } else if (event.key === KEY_TAB) {
            props.onActive(false);
        } else {
            event.preventDefault();
        }
    };

    useEffect(() => {
        !activeRef.current && focusItem === 0 && setFocusItem(DEFAULT_FOCUS_VALUE);
        focusItem === results.length && setFocusItem(focusItem - 1);
        activeRef.current?.focus();
    }, [focusItem, results.length, setFocusItem]);

    const longestItemLength = useMemo(() => {
        return isStringArray
            ? Math.max(...(results as Array<string>).map(el => (el as string).length))
            : Math.max(...(results as Array<Item>).map(el => (el as Item).label.length));
    }, [results, isStringArray]);

    const currentWidth = useMemo(() => {
        let width = 0;
        let scrollBarOffset = 0;
        if (results.length > 10) {
            scrollBarOffset = 16;
        }
        if ((props.width as number) < longestItemLength * letterWidth) {
            width = longestItemLength * letterWidth + scrollBarOffset;
        } else {
            width = props.width as number;
        }
        return width > maxWidth ? maxWidth : width;
    }, [props.width, results, longestItemLength]);

    const isItemSelected = (item: Item | string) =>
        isStringArray
            ? props.value === item
            : (props.value as Item)?.id === (item as Item).id || props.value === (item as Item).label;

    return props.isMenuOpen ? (
        <div
            className={props.variant === Variant.AgGrid ? 'dropdown-content ignoreOutsideClick' : 'dropdown-content'}
            style={{
                width: currentWidth || 'auto',
            }}
            ref={ref}
            tabIndex={props.tabIndex}
        >
            {results.length ? (
                (results as Array<string | Item>).map((item: Item | string, index: number) => {
                    const content = isStringArray ? (item as string) : (item as Item).label;
                    const labelId = isStringArray ? 'id-' + item : (item as Item).id;
                    const isFocused = props.isMenuOpen && focusItem === index;

                    const row = (
                        <div
                            onClick={() => selectOptionHadler(item, index)}
                            onKeyDown={event => onKeyDown(event, item, index)}
                            onMouseMove={() => {
                                setDisableMousePointer(false);
                                setFocusItem(index);
                            }}
                            onMouseEnter={() => {
                                !disableMousePointer && setFocusItem(index);
                            }}
                            className={`${isItemSelected(item) ? 'dropdown-piece-selected' : 'dropdown-piece'} ${
                                disableMousePointer ? 'disableMouse' : ''
                            }`}
                            ref={focusItem === index ? activeRef : undefined}
                            tabIndex={focusItem === index ? 0 : -1}
                        >
                            <Label id={labelId} textOverflow content={content} />
                        </div>
                    );

                    if (!isFocused) {
                        return <React.Fragment key={labelId}>{row}</React.Fragment>;
                    }

                    return (
                        <TsrTooltip
                            key={labelId}
                            visible={isFocused}
                            content={content}
                            onShow={instance => {
                                const label = instance.reference.querySelector('label');
                                if (!label || label.scrollWidth <= label.clientWidth) {
                                    return false;
                                }
                            }}
                        >
                            {row}
                        </TsrTooltip>
                    );
                })
            ) : (
                <div
                    className={
                        props.variant === Variant.AgGrid
                            ? 'dropdown-piece empty-dropdown  ignoreOutsideClick'
                            : 'dropdown-piece empty-dropdown '
                    }
                >
                    <Label content="-" />
                </div>
            )}
        </div>
    ) : (
        <></>
    );
});
